import { Image, YStack, View, XStack, Text, Separator, useMedia, styled } from '@red-ui/components'
import React from 'react'
import { Chip } from '@/components'
import { getString } from '@vrw/data'
import { PUBLIC_URL } from '@/config'
import { differenceInCalendarDays, format } from 'date-fns'
import { formatDuration } from '@vrw/data/src/utils/formatters'
import { Flight } from '../../api/models/flight'

type Props = {
  subtitle?: string
  inSideSheet?: boolean
  data: Flight
}

const DurationText = styled(Text, {
  fontSize: '$3',
  $gtTabletLandscape: { fontSize: '$4' },
  color: '$black6',
})

export const Header = ({ data: { segments, departure, arrival, destination, duration, origin }, subtitle, inSideSheet = false }: Props) => {
  const { gtMobile } = useMedia()

  if (!segments.length) return null

  const segment = segments[0]
  const basicFontSizeConfig = inSideSheet ? '$5' : '$7'
  const logoSize = getLogoSize(gtMobile, inSideSheet)
  const departureDate = new Date(departure)
  const arrivalDate = new Date(arrival)

  const arrivalDayNote = getArrivalDayNote(departureDate, arrivalDate)
  const formattedDepartureTime = format(departureDate, 'HH:mm')
  const formattedArrivalTime = format(arrivalDate, 'HH:mm')

  return (
    <YStack gap="$2" $gtMobile={{ gap: '$3' }}>
      <View
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        $gtMobile={inSideSheet && { flexDirection: 'column-reverse', alignItems: 'flex-start' }}
        $gtTabletLandscape={{ flexDirection: 'row' }}>
        <XStack alignItems="center">
          <Image
            source={{
              uri: `${PUBLIC_URL}/img/virgin_atlantic_logo.png`, // Lack of API: need to fetch the correct airline logo?
              height: logoSize,
              width: logoSize,
            }}
            aria-label={getString('redSkySearch.results.card.logo.alt')}
            marginRight="$2"
          />
          <YStack alignItems="flex-start" justifyContent="space-between">
            <Text
              marginBottom="$0.5"
              lineHeight="$4"
              fontSize="$4"
              fontWeight="600"
              $gtMobile={{
                fontSize: basicFontSizeConfig,
                lineHeight: basicFontSizeConfig,
                marginBottom: inSideSheet ? '$0.5' : '$2',
              }}>
              {`${formattedDepartureTime} - ${formattedArrivalTime}`}
            </Text>
            <Text
              color="$black5"
              fontSize="$3"
              $gtMobile={!inSideSheet && { fontSize: '$5' }}
              $gtTabletLandscape={{ fontSize: inSideSheet ? '$4' : '$6' }}>
              {getString('redSkySearch.results.card.airline').replace('{{airlineName}}', segment.airline.name)}
            </Text>
          </YStack>
        </XStack>
        <Text
          fontSize="$4"
          $gtMobile={{
            fontSize: basicFontSizeConfig,
            lineHeight: basicFontSizeConfig,
            marginBottom: inSideSheet ? '$2' : '$0',
          }}
          $gtTabletLandscape={{ marginBottom: '$0' }}
          fontWeight="600"
          textAlign="right">
          {format(arrivalDate, 'EEE dd MMM')}
        </Text>
      </View>
      <YStack>
        <XStack alignItems="center" justifyContent="space-between">
          <Text
            fontSize="$4"
            fontWeight="600"
            marginRight="$2"
            $gtMobile={{
              fontSize: basicFontSizeConfig,
              lineHeight: basicFontSizeConfig,
              marginRight: inSideSheet ? '$3' : '$4',
            }}>
            {origin.code}
          </Text>
          <Separator borderColor="$black10" />
          <Chip.Root
            marginHorizontal="$1"
            paddingHorizontal="$2"
            minWidth="unset"
            backgroundColor="$white"
            borderWidth={1}
            borderColor="$black10"
            shadowOpacity={0}
            $gtMobile={{ marginHorizontal: '$2', paddingHorizontal: '$3' }}
            $gtTabletLandscape={{ paddingHorizontal: '$4' }}>
            <Chip.Text color="$black6" fontWeight="400" fontSize="$3" $gtTabletLandscape={{ fontSize: '$4' }}>
              {getString('redSkySearch.results.card.flightType.direct')}
            </Chip.Text>
          </Chip.Root>
          <Separator borderColor="$black10" />
          <Text
            textAlign="right"
            fontSize="$4"
            fontWeight="600"
            marginLeft="$2"
            $gtMobile={{
              fontSize: basicFontSizeConfig,
              lineHeight: basicFontSizeConfig,
              marginLeft: inSideSheet ? '$3' : '$4',
            }}>
            {destination.code}
          </Text>
        </XStack>
        <XStack marginTop="$1" $gtTabletLandscape={{ marginTop: '$2' }} justifyContent="space-between">
          <Text
            fontSize={inSideSheet ? '$3' : '$4'}
            $gtMobile={{ fontSize: inSideSheet ? '$3' : '$5' }}
            $gtTabletLandscape={{ fontSize: '$6' }}>
            {formattedDepartureTime}
          </Text>
          <XStack>
            <DurationText>{formatDuration(duration)}</DurationText>
            {arrivalDayNote && <DurationText>{arrivalDayNote}</DurationText>}
          </XStack>
          <Text
            textAlign="right"
            fontSize={inSideSheet ? '$3' : '$4'}
            $gtMobile={{ fontSize: inSideSheet ? '$3' : '$5' }}
            $gtTabletLandscape={{ fontSize: '$6' }}>
            {formattedArrivalTime}
          </Text>
        </XStack>
      </YStack>
      {!!subtitle && (
        <>
          <Text fontSize="$3" $gtTabletLandscape={{ fontSize: '$4' }} textAlign="center">
            {subtitle}
          </Text>
          <Separator />
        </>
      )}
    </YStack>
  )
}

function getLogoSize(gtMobile: boolean, inSideSheet: boolean): number {
  if (!gtMobile) return 35
  return inSideSheet ? 42 : 55
}

function getArrivalDayNote(departureDate: Date, arrivalDate: Date): string | null {
  const offset = differenceInCalendarDays(arrivalDate, departureDate)
  if (offset <= 0) return null

  const key = offset === 1 ? 'redSkySearch.results.card.arrivalDayNote.one' : 'redSkySearch.results.card.arrivalDayNote.other'

  return getString(key).replace('{{arrivalDayOffset}}', `${offset}`)
}
